import { useNavigate } from 'react-router-dom';
import { trpc } from '../utils/trpc.ts';
import '../styles/GroupMemberProfile.css';
import TagManagerModal from './TagManagerModal';
import { useEffect, useState, useMemo } from 'react';

import { useAuth } from '../contexts/AuthContext.tsx';
import { supabase } from '../utils/supabase.ts';

interface Tag {
  id: number;
  name: string;
  category_id?: number;
}

interface BadgeEntry {
  level: number;
  badges:
    | {
        id: string;
        name: string;
        icon_url: string | null;
      }
    | {
        id: string;
        name: string;
        icon_url: string | null;
      }[]
    | null;
}

const AVATAR_GRADIENTS = [
  'linear-gradient(145deg,#fcb31b,#e67919)',
  'linear-gradient(145deg,#4a7a47,#80af7d)',
  'linear-gradient(145deg,#2d4f2b,#4a7a47)',
  'linear-gradient(145deg,#527d4f,#80af7d)',
  'linear-gradient(145deg,#e67919,#fcb31b)',
];

function getGradient(char: string): string {
  return AVATAR_GRADIENTS[char.charCodeAt(0) % AVATAR_GRADIENTS.length];
}

const GroupMemberProfile = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const {
    data: profile,
    isLoading: profileLoading,
    refetch: refetchProfile,
  } = trpc.member.getMyProfile.useQuery();

  const displayName =
    profile?.full_name ?? profile?.username ?? user?.email?.split('@')[0] ?? 'Member';
  const displayHandle = profile?.username ?? user?.email?.split('@')[0] ?? '';
  const displayInitial = displayName.charAt(0).toUpperCase();

  const { data: memberTags, refetch: refetchTags } = trpc.tags.getUserTags.useQuery(
    { userId: profile?.id || '' },
    { enabled: !!profile?.id },
  );

  const { data: memberBadges, isLoading: badgesLoading } =
    trpc.badge.getUserBadges.useQuery(
      { userId: profile?.id || '' },
      { enabled: !!profile?.id },
    );

  const updateVisibility = trpc.member.updateMyProfile.useMutation();

  const [isPrivateOverride, setIsPrivateOverride] = useState<boolean | null>(null);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [activeNav, setActiveNav] = useState<'overview' | 'settings' | 'privacy'>(
    'overview',
  );

  const currentTags: Tag[] = useMemo(() => {
    if (!memberTags || !Array.isArray(memberTags)) return [];
    const getTagName = (entry: {
      tags?: { name?: string } | { name?: string }[] | null;
    }) => {
      const tagValue = entry.tags;
      const tagRecord = Array.isArray(tagValue) ? tagValue[0] : tagValue;
      return tagRecord?.name ?? 'Unknown';
    };
    return memberTags.map((entry) => ({
      id: entry.tag_id,
      name: getTagName(entry),
    }));
  }, [memberTags]);

  const tagNames = currentTags.map((tag) => tag.name);

  const badges = useMemo(() => {
    if (!memberBadges || !Array.isArray(memberBadges)) return [];
    return memberBadges
      .map((entry: BadgeEntry) => {
        const badgeData = Array.isArray(entry.badges) ? entry.badges[0] : entry.badges;
        if (!badgeData) return null;
        return {
          id: badgeData.id,
          name: badgeData.name,
          iconUrl: badgeData.icon_url,
          level: entry.level,
        };
      })
      .filter((badge): badge is NonNullable<typeof badge> => !!badge);
  }, [memberBadges]);

  useEffect(() => {
    if (!profileLoading && !profile) {
      navigate('/dashboard');
    }
  }, [profileLoading, profile, navigate]);

  const effectiveIsPrivate = isPrivateOverride ?? profile?.is_private ?? false;

  const handleVisibilityToggle = () => {
    const previous = effectiveIsPrivate;
    const newValue = !previous;
    setIsPrivateOverride(newValue);
    updateVisibility.mutate(
      { is_private: newValue },
      {
        onError: () => setIsPrivateOverride(previous),
        onSuccess: () => {
          setIsPrivateOverride(null);
          refetchProfile();
        },
      },
    );
  };

  const handleModalClose = async () => {
    setIsTagModalOpen(false);
    await refetchTags();
  };

  if (profileLoading || badgesLoading) {
    return (
      <div className="profilePage">
        <div className="profileBanner">
          <div className="bannerRing bannerRing1" />
          <div className="bannerRing bannerRing2" />
          <div className="bannerRing bannerRing3" />
        </div>
        <p
          style={{
            padding: '2rem',
            fontFamily: 'Comfortaa, sans-serif',
            color: 'rgba(45,79,43,0.5)',
          }}
        >
          Loading profile…
        </p>
      </div>
    );
  }

  return (
    <div className="profilePage">
      {/* ── Banner ── */}
      <div className="profileBanner">
        <div className="bannerRing bannerRing1" />
        <div className="bannerRing bannerRing2" />
        <div className="bannerRing bannerRing3" />
      </div>

      {/* ── Profile Strip ── */}
      <div className="profileStrip">
        <div className="avatarRow">
          <div
            className="profileAvatar"
            style={{ background: getGradient(displayInitial) }}
          >
            {profile?.profile_picture_url ? (
              <img
                onLoad={(e) => e.currentTarget.classList.add('loaded')}
                src={profile.profile_picture_url}
                alt={displayName}
                className="profileAvatarImage"
              />
            ) : (
              displayInitial
            )}
          </div>

          <button
            type="button"
            className="edit-profile-button"
            onClick={() => navigate('/group-member/edit')}
          >
            Edit Profile
          </button>
        </div>

        <div className="nameRow">
          <p className="profileName">
            {profile?.first_name && profile?.last_name
              ? `${profile.first_name} ${profile.last_name}`
              : displayName}
          </p>
          {/* ── Top 3 badges next to name ── */}
          {badges.length > 0 && (
            <div className="topBadges">
              {badges
                .slice()
                .sort((a, b) => b.level - a.level)
                .slice(0, 3)
                .map((badge) => (
                  <div key={badge.id} className="topBadgeItem">
                    {badge.iconUrl ? (
                      <img
                        onLoad={(e) => e.currentTarget.classList.add('loaded')}
                        src={badge.iconUrl}
                        alt={badge.name}
                        className="topBadgeIcon"
                      />
                    ) : (
                      <div className="topBadgeFallback">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polygon points="12,2 15.1,8.3 22,9.3 17,14.1 18.2,21 12,17.8 5.8,21 7,14.1 2,9.3 8.9,8.3" />
                        </svg>
                      </div>
                    )}
                    <div className="topBadgeTooltip">
                      <span className="topBadgeTipName">{badge.name}</span>
                      <span className="topBadgeTipLevel">Level {badge.level}</span>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
        {displayHandle && <p className="profileHandle">@{displayHandle}</p>}
        {profile?.pronouns && <span className="profilePronouns">{profile.pronouns}</span>}

        <div className="statsRow">
          <div className="statItem">
            <div className="statVal">{badges.length}</div>
            <div className="statLbl">Badges</div>
          </div>
          <div className="statItem">
            <div className="statVal">{tagNames.length}</div>
            <div className="statLbl">Interests</div>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="profileBody">
        {/* ── Sidebar ── */}
        <nav className="profileSidebar">
          <span className="navSectionLabel">Profile</span>

          <button
            type="button"
            className={`navItem${activeNav === 'overview' ? ' active' : ''}`}
            onClick={() => setActiveNav('overview')}
          >
            <span className="navIcon">
              <svg viewBox="0 0 24 24">
                <circle cx="12" cy="8" r="4" />
                <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
              </svg>
            </span>
            Overview
          </button>

          <span className="navSectionLabel">Account</span>

          <button
            type="button"
            className={`navItem${activeNav === 'settings' ? ' active' : ''}`}
            onClick={() => setActiveNav('settings')}
          >
            <span className="navIcon">
              <svg viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="3" />
                <path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" />
              </svg>
            </span>
            Settings
          </button>

          <button
            type="button"
            className={`navItem${activeNav === 'privacy' ? ' active' : ''}`}
            onClick={() => setActiveNav('privacy')}
          >
            <span className="navIcon">
              <svg viewBox="0 0 24 24">
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </span>
            Privacy
          </button>
        </nav>

        {/* ── Main ── */}
        <main className="profileMain">
          {/* ── Overview ── */}
          {activeNav === 'overview' && (
            <>
              {/* Interests */}
              <div className="profileBlock">
                <div className="blockHeader">
                  <div className="blockHeaderLeft">
                    <div className="blockDot" />
                    Interests &amp; tags
                  </div>
                  <button
                    data-testid="editTagsButton"
                    className="blockAction"
                    onClick={() => setIsTagModalOpen(true)}
                  >
                    Edit
                  </button>
                </div>
                <div className="blockBody">
                  <div className="tagCloud">
                    {tagNames.length === 0 ? (
                      <p className="emptyTags">No interests added yet.</p>
                    ) : (
                      tagNames.map((name) => (
                        <span key={name} className="tag">
                          {name}
                        </span>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Badges */}
              <div className="profileBlock">
                <div className="blockHeader">
                  <div className="blockHeaderLeft">
                    <div className="blockDot" />
                    Badges
                  </div>
                </div>
                <div className="blockBody">
                  {badges.length === 0 ? (
                    <p className="member-badge-empty">No badges earned yet.</p>
                  ) : (
                    <div className="badgeList">
                      {badges.map((badge) => (
                        <div key={badge.id} className="member-badge-card">
                          {badge.iconUrl ? (
                            <img
                              onLoad={(e) => e.currentTarget.classList.add('loaded')}
                              src={badge.iconUrl}
                              alt={badge.name}
                              className="member-badge-icon"
                            />
                          ) : (
                            <div className="member-badge-fallback">
                              <svg viewBox="0 0 24 24">
                                <polygon points="12,2 15.1,8.3 22,9.3 17,14.1 18.2,21 12,17.8 5.8,21 7,14.1 2,9.3 8.9,8.3" />
                              </svg>
                            </div>
                          )}
                          <div className="member-badge-meta">
                            <p className="member-badge-name">{badge.name}</p>
                            <p className="member-badge-level">Level {badge.level}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* ── Privacy ── */}
          {activeNav === 'privacy' && (
            <div className="profileBlock">
              <div className="blockHeader">
                <div className="blockHeaderLeft">
                  <div className="blockDot" />
                  Profile visibility
                </div>
              </div>
              <div className="visRow">
                <div>
                  <div className="visLabel">
                    {effectiveIsPrivate ? 'Private profile' : 'Public profile'}
                  </div>
                  <div className="visSub">
                    {effectiveIsPrivate
                      ? 'Only you can see your profile'
                      : 'Anyone can view your profile and groups'}
                  </div>
                </div>
                <button
                  type="button"
                  className={`toggleTrack${!effectiveIsPrivate ? ' on' : ''}`}
                  onClick={handleVisibilityToggle}
                  aria-label="Toggle profile visibility"
                >
                  <div className="toggleKnob" />
                </button>
              </div>
            </div>
          )}

          {/* ── Settings ── */}
          {activeNav === 'settings' && (
            <div className="profileBlock">
              <div className="blockHeader">
                <div className="blockHeaderLeft">
                  <div className="blockDot" />
                  Settings
                </div>
              </div>
              <div className="blockBody">
                <p className="emptyTags">Settings coming soon.</p>{' '}
                <button onClick={handleSignOut}>Sign Out</button>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* ── Tag Manager Modal ── */}
      <TagManagerModal
        isOpen={isTagModalOpen}
        onClose={handleModalClose}
        mode="edit"
        initialTags={currentTags}
        originalTags={currentTags}
        profileType="member"
      />
    </div>
  );
};

export default GroupMemberProfile;
